import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, Slider, Chip, Menu, MenuItem, InputAdornment, useMediaQuery, useTheme, Divider, Select, FormControl, InputLabel, CircularProgress, Alert, Link } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import KeyboardDoubleArrowUpIcon from '@mui/icons-material/KeyboardDoubleArrowUp';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import Tips from './dialogs/Tips';
import LifeStageSelector from './LifeStageSelector';
import StyledTextField from './common/StyledTextField';
import { Platform } from '../types/Game';
import { GameReviewData } from '../types/Review';
import { setGameReview, setGameSubStatus, getPlatforms, getGamePlatforms, getUserBirthDate, getGameMemo, saveGameMemo, deleteGameMemo, getGamePlaytimes, saveGamePlaytime, deleteGamePlaytime, getUserGameStatus } from '../services/api';
import { getSubStatusOptions, toggleSubStatus } from '../utils/subStatus';
import ReviewPreviewDialog from './dialogs/ReviewPreviewDialog';
import { scoreTags } from '../utils/scoreTags';
import { useUser } from '../contexts/UserContext';
import { getMemoMaxLength } from '../utils/subscription';
import CharacterCounter from './common/CharacterCounter';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';

// Lexical（~60KB gzip）はメインバンドルから外す。エディタを開いた瞬間に別チャンクをロードする。
const MemoEditorDialog = lazy(() => import('./editor/MemoEditorDialog'));

interface GameReviewDialogProps {
  open: boolean;
  onClose: () => void;
  gameId: number;
  /**
   * 既存のレビューデータを親から渡す（詳細ページ等、すでに status を持っている場面で使う）。
   * 未指定時はダイアログが自身で `getUserGameStatus(gameId)` を呼んで取得する（カードからの起動等）。
   */
  initialData?: GameReviewData;
  platforms: Platform[];
  releaseDate?: string;
  userBirthDate?: string;
  status?: number | null;
  onReviewSaved?: (reviewData: GameReviewData) => void;
}

// 新規（未レビュー）状態を表す空データ。GameCard の新規モード初期値とも共有する。
// モジュール定数として参照を固定することで、親の再レンダリング時に
// GameReviewDialog の初期化 effect が誤って再実行され入力が消える事故を防ぐ。
export const EMPTY_REVIEW_DATA: GameReviewData = {
  score: null,
  reviewText: null,
  playStartDate: null,
  playEndDate: null,
  playTime: null,
  scoreTags: [],
  platform: [],
  scoreVisual: null,
  scoreSound: null,
  scoreGameplay: null,
  scoreNarrative: null,
  scoreOriginality: null,
  subStatus: [],
};

interface PlaytimeData {
  id: number;
  playstyle: number;
  play_time_minutes: number;
  note: string;
}

const PLAYSTYLE_LABELS: { [key: number]: string } = {
  0: 'フリー',
  1: 'メイン',
  2: 'メイン + サブ',
  3: 'コンプリート'
};

const GameReviewDialog: React.FC<GameReviewDialogProps> = ({
  open,
  onClose,
  gameId,
  initialData,
  platforms: initialPlatforms,
  releaseDate,
  userBirthDate: initialBirthDate,
  status,
  onReviewSaved
}) => {
  const { t } = useTranslation('reviewDialog');
  const subStatusOptions = getSubStatusOptions(status);
  const isPrePlayStatus = status === 4 || status === 5;
  const { user } = useUser();
  const navigate = useNavigate();
  // ゲストはステータスのみ保存可能。登録後に使える項目（レビュー本文・メモ・クリアタイム）は
  // 非表示にせず、何が記録できるかが分かるようグレーアウトして表示する
  const isGuest = user?.is_guest === true;
  const memoMaxLength = getMemoMaxLength(user?.subscription_plan ?? 0);

  // ゲストのときは中身をグレーアウト＋操作不可にするだけ。非ゲストは node をそのまま返す
  const withGuestLock = (node: React.ReactNode) =>
    isGuest ? (
      <Box sx={{ opacity: 0.4, pointerEvents: 'none', userSelect: 'none' }} aria-hidden>
        {node}
      </Box>
    ) : node;
  const [tempData, setTempData] = useState<GameReviewData>(initialData ?? EMPTY_REVIEW_DATA);
  // 差分検知用の初期スナップショット。initialData が無いケース（自己fetch）でも正確に dirty 判定するため別管理。
  const [initialReviewSnapshot, setInitialReviewSnapshot] = useState<GameReviewData>(initialData ?? EMPTY_REVIEW_DATA);
  // 自己fetch中のローディング状態。initialData 渡しの場合は loading 不要。
  const [isLoadingInitial, setIsLoadingInitial] = useState<boolean>(!initialData);
  const [availablePlatforms, setAvailablePlatforms] = useState<Platform[]>([]);
  const [platforms, setPlatforms] = useState<Platform[]>(initialPlatforms);
  const [userBirthDate, setUserBirthDate] = useState<string | null>(initialBirthDate || null);
  const [isDialogDirty, setIsDialogDirty] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [memo, setMemo] = useState<string>('');
  const [initialMemo, setInitialMemo] = useState<string>('');
  const [memoEditorOpen, setMemoEditorOpen] = useState(false);
  const [playtimeRecords, setPlaytimeRecords] = useState<PlaytimeData[]>([]);
  const [selectedPlaystyle, setSelectedPlaystyle] = useState<number>(1);
  const [playtimeHours, setPlaytimeHours] = useState<string>('');
  const [playtimeMinutes, setPlaytimeMinutes] = useState<string>('');
  const [playtimeNote, setPlaytimeNote] = useState<string>('');
  const [initialPlaytimeData, setInitialPlaytimeData] = useState<{ hours: string; minutes: string; note: string }>({ hours: '', minutes: '', note: '' });

  // 初期化に使う props は ref で読む。依存配列に入れると、親が再レンダリングして
  // initialData / platforms の「参照だけ」が変わった瞬間（例: UserContext の5分ごとの
  // ユーザー再取得）に初期化 effect が再実行され、編集中の入力が初期値に巻き戻る。
  // 初期化はダイアログを開いた瞬間の一度きりでよいので、参照の変化に影響されない形にする。
  const initialDataRef = useRef(initialData);
  const initialPlatformsRef = useRef(initialPlatforms);
  const initialBirthDateRef = useRef(initialBirthDate);
  initialDataRef.current = initialData;
  initialPlatformsRef.current = initialPlatforms;
  initialBirthDateRef.current = initialBirthDate;

  useEffect(() => {
    if (!open) return;

    const openedInitialData = initialDataRef.current;
    const openedPlatforms = initialPlatformsRef.current;
    const openedBirthDate = initialBirthDateRef.current;

    // 開き直しに備えて編集状態をリセット（ダイアログを閉じても親がアンマウントしない場合がある）
    setIsDialogDirty(false);
    setIsLoadingInitial(!openedInitialData);

    // initialData が無ければ自己 fetch（ハイブリッド動作）。並列化のため Promise として作る。
    const initialPromise: Promise<GameReviewData> = openedInitialData
      ? Promise.resolve(openedInitialData)
      : getUserGameStatus(gameId)
          .then(({ status: _omitStatus, ...rest }) => rest as GameReviewData)
          .catch((e) => {
            // eslint-disable-next-line no-console
            console.error('ステータスの取得に失敗しました', e);
            return EMPTY_REVIEW_DATA;
          });

    // 閉じられた／別ゲームに切り替わった後に古いレスポンスが state を上書きするのを防ぐ
    let cancelled = false;

    const fetchData = async () => {
      const [resolvedInitial, availablePlatformsData, gamePlatforms, birthDate, memoData, playtimeData] = await Promise.all([
        initialPromise,
        getPlatforms(),
        !openedPlatforms.length ? getGamePlatforms(gameId) : Promise.resolve(openedPlatforms),
        !openedBirthDate ? getUserBirthDate() : Promise.resolve(openedBirthDate),
        getGameMemo(gameId).catch(() => ({ data: { memo: '' } })),
        getGamePlaytimes(gameId).catch(() => ({ data: [] }))
      ]);

      if (cancelled) return;

      // 既存レビューデータの反映（fetched or props）
      setTempData(resolvedInitial);
      setInitialReviewSnapshot(resolvedInitial);

      setAvailablePlatforms(availablePlatformsData);
      setPlatforms(gamePlatforms);
      setUserBirthDate(birthDate);
      const memoText = memoData.data.memo || '';
      setPlaytimeRecords(playtimeData.data);
      setMemo(memoText);
      setInitialMemo(memoText);

      // プラットフォーム未設定でゲームが1PF専用ならその1つを自動選択（既存挙動）
      setTempData(prev => ({
        ...prev,
        platform: prev.platform.length > 0
          ? prev.platform
          : (gamePlatforms.length === 1 ? [gamePlatforms[0].platform_id] : [])
      }));

      setIsLoadingInitial(false);
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [open, gameId]);

  // プレイスタイル変更時のハンドラー
  const handlePlaystyleChange = (newPlaystyle: number) => {
    setSelectedPlaystyle(newPlaystyle);
    const existingRecord = playtimeRecords.find(r => r.playstyle === newPlaystyle);
    if (existingRecord) {
      const hours = Math.floor(existingRecord.play_time_minutes / 60);
      const minutes = existingRecord.play_time_minutes % 60;
      const hoursStr = hours.toString();
      const minutesStr = minutes.toString();
      const noteStr = existingRecord.note;
      setPlaytimeHours(hoursStr);
      setPlaytimeMinutes(minutesStr);
      setPlaytimeNote(noteStr);
      setInitialPlaytimeData({ hours: hoursStr, minutes: minutesStr, note: noteStr });
    } else {
      setPlaytimeHours('');
      setPlaytimeMinutes('');
      setPlaytimeNote('');
      setInitialPlaytimeData({ hours: '', minutes: '', note: '' });
    }
  };

  // プレイタイム保存ハンドラー
  const handleSavePlaytime = async () => {
    try {
      const hours = parseInt(playtimeHours) || 0;
      const minutes = parseInt(playtimeMinutes) || 0;
      const totalMinutes = hours * 60 + minutes;

      if (totalMinutes === 0) {
        alert(t('alerts.enterPlaytime'));
        return;
      }

      // フリースタイル（0）の場合はノート必須
      if (selectedPlaystyle === 0 && !playtimeNote.trim()) {
        alert(t('alerts.freeNoteRequired'));
        return;
      }

      // saveGamePlaytimeはPOSTでupsert（作成or更新）に対応
      await saveGamePlaytime(gameId, selectedPlaystyle, totalMinutes, playtimeNote);

      // データを再取得
      const playtimeData = await getGamePlaytimes(gameId);
      setPlaytimeRecords(playtimeData.data);
    } catch (error) {
      console.error('プレイタイムの保存に失敗しました', error);
    }
  };

  // プレイタイム削除ハンドラー
  const handleDeletePlaytime = async () => {
    try {
      const existingRecord = playtimeRecords.find(r => r.playstyle === selectedPlaystyle);
      if (!existingRecord) return;

      if (window.confirm(t('alerts.deletePlaytimeConfirm'))) {
        await deleteGamePlaytime(gameId, selectedPlaystyle);
        setPlaytimeRecords(playtimeRecords.filter(r => r.playstyle !== selectedPlaystyle));
        setPlaytimeHours('');
        setPlaytimeMinutes('');
        setPlaytimeNote('');
      }
    } catch (error) {
      console.error('プレイタイムの削除に失敗しました', error);
    }
  };

  const handleSave = async () => {
    try {
      // レビューデータが変更されているかチェック（subStatusは別API保存なので除外）
      // initialReviewSnapshot は initialData props または自己fetch結果のどちらかを保持
      const { subStatus: tempSubStatus, ...tempReviewFields } = tempData;
      const { subStatus: initSubStatus, ...initReviewFields } = initialReviewSnapshot;
      const isReviewChanged = JSON.stringify(tempReviewFields) !== JSON.stringify(initReviewFields);
      const isSubStatusChanged = JSON.stringify(tempSubStatus) !== JSON.stringify(initSubStatus);

      // メモが変更されているかチェック
      const isMemoChanged = memo !== initialMemo;

      // プレイタイムが変更されているかチェック
      const isPlaytimeChanged =
        playtimeHours !== initialPlaytimeData.hours ||
        playtimeMinutes !== initialPlaytimeData.minutes ||
        playtimeNote !== initialPlaytimeData.note;

      // setGameReview と setGameSubStatus は同じ UserGameStatus 行を更新するため逐次実行
      // （並列だと後勝ちで一方の変更が消える）
      if (isReviewChanged) {
        await setGameReview(
          gameId,
          tempData.score,
          tempData.reviewText,
          tempData.playStartDate,
          tempData.playEndDate,
          tempData.playTime,
          tempData.scoreTags,
          tempData.platform,
          tempData.scoreVisual,
          tempData.scoreSound,
          tempData.scoreGameplay,
          tempData.scoreNarrative,
          tempData.scoreOriginality
        );
      }

      if (isSubStatusChanged) {
        await setGameSubStatus(gameId, tempSubStatus);
      }

      // メモ・プレイタイムは別テーブルなので並列で安全
      const parallelPromises: Promise<unknown>[] = [];
      if (isMemoChanged) {
        parallelPromises.push(saveGameMemo(gameId, memo));
      }
      if (isPlaytimeChanged) {
        const hours = parseInt(playtimeHours) || 0;
        const minutes = parseInt(playtimeMinutes) || 0;
        const totalMinutes = hours * 60 + minutes;

        if (totalMinutes > 0) {
          if (selectedPlaystyle === 0 && !playtimeNote.trim()) {
            alert(t('alerts.freeNoteRequired'));
            return;
          }
          parallelPromises.push(saveGamePlaytime(gameId, selectedPlaystyle, totalMinutes, playtimeNote));
        }
      }

      if (parallelPromises.length > 0) {
        await Promise.all(parallelPromises);
      }

      onReviewSaved?.(tempData);
      onClose();
    } catch (error) {
      console.error('レビューの保存に失敗しました', error);
    }
  };

  // 未保存の編集がある状態での閉じる操作には確認を出す（メモ編集ダイアログと同じ挙動）。
  // 保存成功時は handleSave 内で onClose() を直接呼ぶため、この確認は通らない。
  const requestClose = () => {
    if (isDialogDirty && !window.confirm(t('alerts.unsavedConfirm'))) {
      return;
    }
    onClose();
  };

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const [platformMenuAnchorEl, setPlatformMenuAnchorEl] = React.useState<null | HTMLElement>(null);
  const [platformSearch, setPlatformSearch] = React.useState('');

  const scoreMarks = [
    { value: 1, label: '1.0' },
    { value: 2, label: t('scoreMarkPoor') },
    { value: 3, label: '3.0' },
    { value: 4, label: t('scoreMarkGood') },
    { value: 5, label: '5.0' },
  ];

  const detailScoreMarks = [
    { value: 1 },
    { value: 2 },
    { value: 3 },
    { value: 4 },
    { value: 5 },
  ];

  const handleScoreChange = (event: Event, newValue: number | number[]) => {
    setTempData({ ...tempData, score: newValue as number });
    setIsDialogDirty(true);
  };

  const handleDetailScoreChange = (field: keyof GameReviewData, event: Event, newValue: number | number[]) => {
    setTempData({ ...tempData, [field]: newValue as number });
    setIsDialogDirty(true);
  };

  const handleScoreTagsChange = (tagId: number) => {
    const newTempScoreTags = tempData.scoreTags.includes(tagId)
      ? tempData.scoreTags.filter((id) => id !== tagId).sort((a, b) => a - b)
      : tempData.scoreTags.length >= 3
        ? tempData.scoreTags
        : [...tempData.scoreTags, tagId].sort((a, b) => a - b);
    setTempData({ ...tempData, scoreTags: newTempScoreTags });
    setIsDialogDirty(true);
  };

  const handleSubStatusChange = (code: number) => {
    const next = toggleSubStatus(tempData.subStatus, code);
    setTempData({ ...tempData, subStatus: next });
    setIsDialogDirty(true);
  };

  const handlePlatformChange = (platformId: number) => {
    const newPlatform = tempData.platform.includes(platformId)
      ? tempData.platform.filter(id => id !== platformId).sort((a, b) => a - b)
      : tempData.platform.length >= 2
        ? tempData.platform
        : [...tempData.platform, platformId].sort((a, b) => a - b);
    
    setTempData({ ...tempData, platform: newPlatform });
    setIsDialogDirty(true);
  };

  const handlePlatformMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setPlatformMenuAnchorEl(event.currentTarget);
  };

  const handlePlatformMenuClose = () => {
    setPlatformMenuAnchorEl(null);
    setPlatformSearch('');
  };

  const handlePlatformSelect = (platformId: number) => {
    handlePlatformChange(platformId);
    handlePlatformMenuClose();
  };

  const filteredPlatforms = availablePlatforms
    .filter(p => !platforms.some(defaultP => defaultP.platform_id === p.platform_id))
    .filter(p => 
      p.name.toLowerCase().includes(platformSearch.toLowerCase())
    );

  const getScoreText = (score: number | null | undefined) => {
    if (score === null || score === undefined) {
      return 'N/A';
    }
    const numericScore = typeof score === 'string' ? parseFloat(score) : score;
    return isNaN(numericScore) ? t('notRated') : numericScore.toFixed(1);
  };

  const getScoreColor = (score: number | null) => {
    if (score === null) return "#666666";
    if (score >= 4.5) return "#4CAF50";
    if (score >= 4.0) return "#8BC34A";
    if (score >= 3.5) return "#CDDC39";
    if (score >= 3.0) return "#FFEB3B";
    if (score >= 2.5) return "#FFC107";
    if (score >= 2.1) return "#FF9800";
    return "#F44336";
  };

  const getDetailScoreStyle = (score: number | null | undefined) => {
    const hasScore = score !== null && score !== undefined;
    return {
      '& .MuiSlider-thumb': {
        width: 12,
        height: 12,
        color: hasScore ? 'primary.main' : 'grey.500',
      },
      '& .MuiSlider-track': {
        color: hasScore ? 'primary.main' : 'grey.500',
      },
      '& .MuiSlider-rail': {
        color: hasScore ? 'primary.light' : 'grey.300',
      },
    };
  };

  const reviewCorrectionTips = (
    <Box>
      <Typography variant="body2" paragraph>{t('correctionTips.intro1')}</Typography>
      <Typography variant="body2" paragraph>{t('correctionTips.intro2')}</Typography>
      <Typography variant="body2" paragraph>{t('correctionTips.intro3')}</Typography>
      <Typography variant="body2" paragraph>{t('correctionTips.intro4')}</Typography>
      <Typography variant="body2" paragraph>{t('correctionTips.intro5')}</Typography>
      <Typography variant="body2" paragraph>{t('correctionTips.intro6')}</Typography>
      <Divider sx={{ my: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <Chip label={t('correctionTips.devPubLabel')} color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">{t('correctionTips.devPubDesc')}</Typography>
        </Box>
        <Box>
          <Chip label={t('correctionTips.contextLabel')} color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">{t('correctionTips.contextDesc')}</Typography>
        </Box>
        <Box>
          <Chip label={t('correctionTips.entryLabel')} color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">{t('correctionTips.entryDesc')}</Typography>
        </Box>
        <Box>
          <Chip label={t('correctionTips.expectationLabel')} color="primary" variant="outlined" sx={{ mb: 1 }} />
          <Typography variant="body2">{t('correctionTips.expectationDesc')}</Typography>
        </Box>
      </Box>
    </Box>
  );

  const subStatusTips = (
    <Box>
      <Typography variant="body2" paragraph>{t('subStatusTips.intro')}</Typography>
      <Typography variant="body2" paragraph>
        <strong>{t('subStatusTips.replayLabel')}</strong>: {t('subStatusTips.replayDesc')}
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>{t('subStatusTips.completeLabel')}</strong>: {t('subStatusTips.completeDesc')}
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>{t('subStatusTips.unclearedLabel')}</strong>: {t('subStatusTips.unclearedDesc')}
      </Typography>
      <Typography variant="body2" paragraph>
        <strong>{t('subStatusTips.retiredLabel')}</strong>: {t('subStatusTips.retiredDesc')}
      </Typography>
    </Box>
  );

  const reviewFormatTips = (
    <Box>
      <Typography 
        variant="caption" 
        sx={{ 
          display: 'block', 
          mb: 0.5,
          color: 'text.secondary',
          fontStyle: 'italic',
          lineHeight: 1.2
        }}
      >
        {t('formatTipsHint')}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {[
          {
            title: t('format.headingTitle'),
            examples: [
              { syntax: `[h1]${t('format.header')}[/h1]`, result: t('format.header') },
              { syntax: `[h2]${t('format.header')}[/h2]`, result: t('format.header') },
            ]
          },
          {
            title: t('format.decorationTitle'),
            examples: [
              { syntax: `[b]${t('format.bold')}[/b]`, result: t('format.bold') },
              { syntax: `[i]${t('format.italic')}[/i]`, result: t('format.italic') },
              { syntax: `[s]${t('format.strike')}[/s]`, result: t('format.strike') },
              { syntax: `[u]${t('format.underline')}[/u]`, result: t('format.underline') }
            ]
          },
          {
            title: t('format.listTitle'),
            examples: [
              { syntax: `[list]\n[*]${t('format.listItem')}\n[*]${t('format.listItem')}\n[/list]`, result: t('format.bullet') },
              { syntax: `[olist]\n[*]${t('format.listItem')}\n[*]${t('format.listItem')}\n[/olist]`, result: t('format.numbered') }
            ]
          },
          {
            title: t('format.otherTitle'),
            examples: [
              { syntax: `[quote]${t('format.quoteSample')}[/quote]`, result: t('format.quote') },
              { syntax: `[spoiler]${t('format.spoiler')}[/spoiler]`, result: t('format.spoiler') },
            ]
          }
        ].map((section) => (
          <Box key={section.title}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'primary.main' }}>
              {section.title}
            </Typography>
            {section.examples.map((example, index) => (
              <Box
                key={index}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: 1,
                  mb: 0.5,
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.03)',
                  }
                }}
              >
                <Box
                  component="code"
                  onClick={() => navigator.clipboard.writeText(example.syntax)}
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.3)',
                    p: 0.5,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    fontSize: '0.65rem',
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.4)',
                    }
                  }}
                >
                  {example.syntax}
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {example.result}
                </Typography>
              </Box>
            ))}
          </Box>
        ))}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={requestClose}
        fullScreen={fullScreen}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          style: {
            backgroundColor: 'rgba(0, 0, 0, 0.65)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '12px',
          },
        }}
      >
        <DialogTitle>{t('title')}</DialogTitle>
        {isGuest && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Alert severity="info">
              ゲスト利用中です。テキストレビューやメモなどの一部機能は制限されています。
              <Box sx={{ mt: 0.5 }}>
                <Link
                  component="button"
                  type="button"
                  onClick={() => navigate('/login')}
                  sx={{ fontWeight: 600 }}
                >
                  アカウント登録（無料） →
                </Link>
              </Box>
            </Alert>
          </Box>
        )}
        <DialogContent>
          {isLoadingInitial && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
              <CircularProgress />
            </Box>
          )}
          <Box sx={{ my: 2, display: (isLoadingInitial || isPrePlayStatus) ? 'none' : undefined }}>
            {/* 総合スコア */}
            <Box sx={{ mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography sx={{ lineHeight: 1 }}>
                  {t('score')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="h6"
                    sx={{
                      color: "white",
                      fontSize: "0.9rem",
                      fontWeight: '600',
                      py: 0,
                      px: 1.25,
                      borderRadius: 1.5,
                      backgroundColor: `${getScoreColor(tempData.score)}B3`,
                      textAlign: 'center'
                    }}
                  >
                    {getScoreText(tempData.score)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1 }}>
                    {t('scoreBaseline')}
                  </Typography>
                </Box>
              </Box>
              <Slider
                value={tempData.score ?? 3}
                onChange={handleScoreChange}
                aria-labelledby="game-score-slider"
                step={0.1}
                marks={scoreMarks}
                min={1}
                max={5}
                valueLabelDisplay="auto"
                sx={{
                  '& .MuiSlider-thumb': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.main' : 'grey.500',
                    width: 18,
                    height: 18,
                  },
                  '& .MuiSlider-track': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.main' : 'grey.500',
                    height: 3,
                  },
                  '& .MuiSlider-rail': {
                    color: tempData.score !== null && tempData.score !== undefined ? 'primary.light' : 'grey.300',
                    height: 3,
                  },
                }}
              />
            </Box>

            {/* 詳細スコア */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <Typography>
                  {t('criteria')}
                </Typography>
                <Tips
                  title={t('scoreGuideTitle')}
                  maxWidth="sm"
                  buttonComponent={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <HelpOutlineIcon 
                        fontSize="small" 
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }} 
                      />
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: { xs: "0.75rem", sm: "0.8rem" },
                          cursor: 'pointer',
                          color: 'rgba(255, 255, 255, 0.7)',
                          lineHeight: 1,
                          '&:hover': { 
                            textDecoration: 'underline',
                            color: 'rgba(255, 255, 255, 0.9)'
                          }
                        }}
                      >
                        {t('viewScoreGuide')}
                      </Typography>
                    </Box>
                  }
                  content={
                    <Box>
                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        {t('guide.visualTitle')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {t('guide.visualList')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        {t('guide.visualDesc')}
                      </Typography>

                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        {t('guide.soundTitle')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {t('guide.soundList')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        {t('guide.soundDesc')}
                      </Typography>

                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        {t('guide.gameplayTitle')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {t('guide.gameplayList')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        {t('guide.gameplayDesc')}
                      </Typography>

                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        {t('guide.narrativeTitle')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {t('guide.narrativeList')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 3, color: '#dadada' }}>
                        {t('guide.narrativeDesc')}
                      </Typography>

                      <Typography variant="h6" sx={{ mb: 1, color: 'primary.main' }}>
                        {t('guide.originalityTitle')}
                      </Typography>
                      <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-line' }}>
                        {t('guide.originalityList')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#dadada' }}>
                        {t('guide.originalityDesc')}
                      </Typography>
                      
                      {/* <Divider sx={{ my: 3 }} />
                      
                      <Box sx={{ 
                        p: 2, 
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        border: '1px solid rgba(255, 255, 255, 0.1)'
                      }}>
                        <Typography variant="h6" sx={{ mb: 1.5, color: 'text.primary' }}>
                          時代性を考慮した評価について
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 1.5, color: '#dadada' }}>
                          ゲームの評価において、そのタイトルがリリースされた時代背景を理解することは非常に重要です。技術的制約、当時の業界標準、プラットフォームの特性、社会情勢などを踏まえて評価することで、より公正で建設的な判断が可能になります。
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#dadada' }}>
                          例えば、1990年代のゲームを現在の技術基準で評価するのではなく、当時の技術的限界の中でどれだけ革新的だったか、同世代の他作品と比較してどうだったかを考慮してください。また、現在では一般的になったシステムや表現手法も、それらの発展に貢献した作品には相応の評価を与えるべきです。
                        </Typography>
                         <Typography variant="body2" sx={{ color: '#dadada' }}>
                         ただし、時代背景に詳しくなくても心配する必要はありません。
                         まずは自分が感じたままの体験を大切にし、その上で発売年やプラットフォームなどの情報を参考程度に活用し、時代性を考慮してみる姿勢を。
                         現在の視点からの素直な感想も、そのゲームの価値を測る重要な指標の一つです。
                        </Typography>
                      </Box> */}
                    </Box>
                  }
                />
              </Box>
              <Box sx={{ 
                display: 'grid', 
                gap: 0.5,
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Visual
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreVisual ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreVisual', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{
                        flex: 1,
                        '@media (pointer: coarse)': {
                          py: 1.75,
                        },
                        ...getDetailScoreStyle(tempData.scoreVisual)
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreVisual)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreVisual)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Sound
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreSound ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreSound', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{
                        flex: 1,
                        '@media (pointer: coarse)': {
                          py: 1.75,
                        },
                        ...getDetailScoreStyle(tempData.scoreSound)
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreSound)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreSound)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Gameplay
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreGameplay ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreGameplay', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{
                        flex: 1,
                        '@media (pointer: coarse)': {
                          py: 1.75,
                        },
                        ...getDetailScoreStyle(tempData.scoreGameplay)
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreGameplay)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreGameplay)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Narrative
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreNarrative ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreNarrative', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{
                        flex: 1,
                        '@media (pointer: coarse)': {
                          py: 1.75,
                        },
                        ...getDetailScoreStyle(tempData.scoreNarrative)
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreNarrative)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreNarrative)}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Typography variant="body2" sx={{ 
                    minWidth: '80px', 
                    fontSize: '0.8rem',
                    color: 'text.secondary'
                  }}>
                    Originality
                  </Typography>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    flex: 1 
                  }}>
                    <Slider
                      value={tempData.scoreOriginality ?? 3}
                      onChange={(event, newValue) => handleDetailScoreChange('scoreOriginality', event, newValue)}
                      step={0.1}
                      min={1}
                      max={5}
                      marks={detailScoreMarks}
                      valueLabelDisplay="auto"
                      size="small"
                      sx={{
                        flex: 1,
                        '@media (pointer: coarse)': {
                          py: 1.75,
                        },
                        ...getDetailScoreStyle(tempData.scoreOriginality)
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        minWidth: '30px',
                        textAlign: 'center',
                        color: "white",
                        fontSize: "0.7rem",
                        fontWeight: '500',
                        lineHeight: 1,
                        py: 0.25,
                        px: 0.75,
                        borderRadius: 1,
                        backgroundColor: `${getScoreColor(tempData.scoreOriginality)}B3`,
                      }}
                    >
                      {getScoreText(tempData.scoreOriginality)}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* レビュー本文は user_game_reviews（別テーブル）。ゲストはグレーアウト表示 */}
          {!isPrePlayStatus && withGuestLock(
            <>
              <Box sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                mt: -2
              }}>
                <Button
                  color="primary"
                  size="small"
                  onClick={() => setPreviewOpen(true)}
                  disabled={!tempData.reviewText}
                  sx={{
                    fontSize: { xs: "0.55rem", sm: "0.6rem" },
                    minWidth: 'auto',
                    p: '2px 8px'
                  }}
                >
                  {t('preview')}
                </Button>
                <Tips
                  title={t('formatTipsTitle')}
                  content={reviewFormatTips}
                  buttonComponent={
                    <Typography
                      variant="body2"
                      color="primary"
                      sx={{
                        fontSize: { xs: "0.55rem", sm: "0.6rem" },
                        cursor: 'pointer',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                    >
                      {t('availableFormats')}
                    </Typography>
                  }
                />
              </Box>
              <Box sx={{ mt: 0.25, mb: 2 }}>
                <StyledTextField
                  id="review"
                  label={t('reviewLabel')}
                  value={tempData.reviewText || ''}
                  onChange={(value) => {
                    setTempData({ ...tempData, reviewText: value });
                    setIsDialogDirty(true);
                  }}
                  multiline
                  minRows={fullScreen ? 6 : 4}
                  maxRows={12}
                  placeholder={t('reviewPlaceholder')}
                />
              </Box>
            </>
          )}

          {!isPrePlayStatus && (
          <Box sx={{ my: 2 }}>
            <Box sx={{ mb: 0 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                  label={t('playStartDate')}
                  type="date"
                  value={tempData.playStartDate || ''}
                  onChange={(e) => {
                    const input = e.target.value;
                    setTempData({ ...tempData, playStartDate: input || null });
                    setIsDialogDirty(true);
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: "9999-12-31" }}
                  size="small"
                  sx={{ flex: 1 }}
                />
                <LifeStageSelector
                  birthDate={userBirthDate}
                  targetDate={releaseDate}
                  onSelect={(date) => {
                    setTempData({ ...tempData, playStartDate: date });
                    setIsDialogDirty(true);
                  }}
                  mode="start"
                />
              </Box>
              {!tempData.playStartDate && (
                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ display: 'block', mt: 0.5, ml: 0.5, lineHeight: 1.2 }}
                >
                  {t('lifeStageHint')}
                </Typography>
              )}
              {tempData.playStartDate && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                  <TextField
                    label={t('playEndDate')}
                    type="date"
                    value={tempData.playEndDate || ''}
                    onChange={(e) => {
                      const input = e.target.value;
                      setTempData({ ...tempData, playEndDate: input || null });
                      setIsDialogDirty(true);
                    }}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ 
                      max: "9999-12-31",
                      min: tempData.playStartDate
                    }}
                    size="small"
                    sx={{ flex: 1 }}
                  />
                  <LifeStageSelector
                    birthDate={userBirthDate}
                    targetDate={releaseDate}
                    onSelect={(date) => {
                      setTempData({ ...tempData, playEndDate: date });
                      setIsDialogDirty(true);
                    }}
                    mode="end"
                    startDate={tempData.playStartDate}
                  />
                </Box>
              )}
            </Box>
            
            <TextField
              margin="dense"
              id="playTime"
              label={t('playTime')}
              type="number"
              fullWidth
              value={tempData.playTime || ''}
              onChange={(e) => {
                const value = e.target.value;
                setTempData({ 
                  ...tempData, 
                  playTime: value === '' ? null : Number(value)  // 空文字列の場合はnullを設定
                });
                setIsDialogDirty(true);
              }}
              InputProps={{
                endAdornment: <InputAdornment position="end">{t('hoursUnit')}</InputAdornment>,
              }}
              size="small"
            />
          </Box>
          )}

          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography>{t('platform')}</Typography>
              <Typography 
                color="text.secondary" 
                variant="body2"
                sx={{ ml: 1 }}
              >
                {tempData.platform.length}/2
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              {platforms.map((platform) => (
                <Chip
                  key={platform.platform_id}
                  label={platform.name}
                  onClick={() => handlePlatformChange(platform.platform_id)}
                  color={tempData.platform.includes(platform.platform_id) ? 'primary' : 'default'}
                  size="small"
                  disabled={!tempData.platform.includes(platform.platform_id) && tempData.platform.length >= 2}
                />
              ))}
              
              {availablePlatforms
                .filter(p => 
                  !platforms.some(defaultP => defaultP.platform_id === p.platform_id) &&
                  tempData.platform.includes(p.platform_id)
                )
                .map((platform) => (
                  <Chip
                    key={platform.platform_id}
                    label={platform.name}
                    onDelete={() => handlePlatformChange(platform.platform_id)}
                    color="primary"
                    size="small"
                  />
                ))}
              
              <Chip
                icon={<AddIcon />}
                label={t('other')}
                onClick={handlePlatformMenuOpen}
                variant="outlined"
                size="small"
                sx={{ cursor: 'pointer' }}
                disabled={tempData.platform.length >= 2}
              />

              <Menu
                anchorEl={platformMenuAnchorEl}
                open={Boolean(platformMenuAnchorEl)}
                onClose={handlePlatformMenuClose}
                PaperProps={{
                  style: {
                    maxHeight: 300,
                    width: '300px',
                  },
                }}
              >
                <Box sx={{ p: 1 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {t('platformNote')}
                  </Typography>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder={t('platformSearchPlaceholder')}
                    value={platformSearch}
                    onChange={(e) => setPlatformSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                    onClick={(e) => e.stopPropagation()}
                  />
                </Box>
                {filteredPlatforms.length === 0 ? (
                  <MenuItem disabled>
                    {t('noPlatform')}
                  </MenuItem>
                ) : (
                  filteredPlatforms.map((platform) => (
                    <MenuItem
                      key={platform.platform_id}
                      onClick={() => handlePlatformSelect(platform.platform_id)}
                      selected={tempData.platform.includes(platform.platform_id)}
                    >
                      {platform.name}
                    </MenuItem>
                  ))
                )}
              </Menu>
            </Box>
          </Box>

          {!isPrePlayStatus && (
            <Box sx={{ my: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography>{t('scoreCorrection')}</Typography>
                <Tips title={t('scoreCorrectionTitle')} content={reviewCorrectionTips} maxWidth="sm"/>
                <Typography
                  color="text.secondary"
                  variant="body2"
                  sx={{ ml: 1 }}
                >
                  {tempData.scoreTags.length}/3
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {scoreTags.map((tag) => (
                  <Chip
                    key={tag.id}
                    icon={
                      tag.id === 9 || tag.id >= 30 ? (
                        <KeyboardDoubleArrowDownIcon style={{ color: '#79bcf3' }} />
                      ) : (
                        <KeyboardDoubleArrowUpIcon style={{ color: '#f37979' }} />
                      )
                    }
                    label={t(`scoreTags.${tag.id}`, { defaultValue: tag.label })}
                    onClick={() => handleScoreTagsChange(tag.id)}
                    color={tempData.scoreTags.includes(tag.id) ? 'primary' : 'default'}
                    size="small"
                    disabled={!tempData.scoreTags.includes(tag.id) && tempData.scoreTags.length >= 3}
                    sx={{
                      '& .MuiChip-icon': { fontSize: '0.7rem' },
                      '& .MuiChip-label': {
                        fontSize: '0.6rem',
                      }
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {!isPrePlayStatus && subStatusOptions.length > 0 && (
            <Box sx={{ my: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography>{t('subStatus')}</Typography>
                <Tips title={t('subStatusTitle')} content={subStatusTips} />
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {subStatusOptions.map((option) => {
                  const selected = tempData.subStatus.includes(option.code);
                  return (
                    <Chip
                      key={option.code}
                      icon={React.cloneElement(option.icon, {
                        style: option.danger && !selected ? { color: '#f37979' } : undefined,
                      })}
                      label={option.label}
                      onClick={() => handleSubStatusChange(option.code)}
                      color={selected ? (option.danger ? 'error' : 'primary') : 'default'}
                      size="small"
                      sx={{
                        '& .MuiChip-icon': { fontSize: '0.9rem' },
                        '& .MuiChip-label': { fontSize: '0.7rem' },
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {!isPrePlayStatus && <Divider sx={{ my: 3 }} />}

          {/* 簡易メモ（user_game_memos＝別テーブル。ゲストはグレーアウト表示） */}
          {withGuestLock(
          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography>{t('memo')}</Typography>
                <Button
                  size="small"
                  onClick={() => setMemoEditorOpen(true)}
                  startIcon={<OpenInFullIcon sx={{ fontSize: '0.8rem !important' }} />}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.7rem',
                    color: 'text.secondary',
                    minHeight: 0,
                    px: 0.75,
                    py: 0.25,
                    '&:hover': { color: 'primary.main' },
                  }}
                >
                  {t('openEditor')}
                </Button>
              </Box>
              <CharacterCounter value={memo} max={memoMaxLength} />
            </Box>
            <StyledTextField
              id="memo"
              label=""
              value={memo}
              onChange={(value) => setMemo(value)}
              multiline
              minRows={2}
              maxRows={4}
              placeholder={t('memoPlaceholder', { max: memoMaxLength })}
              slotProps={{ htmlInput: { maxLength: memoMaxLength } }}
            />
          </Box>
          )}

          {/* クリアタイム（user_game_playtimes＝別テーブル。ゲストはグレーアウト表示） */}
          {!isPrePlayStatus && <Divider sx={{ my: 3 }} />}
          {!isPrePlayStatus && withGuestLock(
          <>
          <Box sx={{ my: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography>{t('clearTime')}</Typography>
              <Tips
                title={t('clearTimeTitle')}
                content={
                  <Box>
                    <Typography variant="body2" paragraph>
                      <strong>{t('clearTimeTips.mainLabel')}</strong>: {t('clearTimeTips.mainDesc')}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>{t('clearTimeTips.mainSubLabel')}</strong>: {t('clearTimeTips.mainSubDesc')}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>{t('clearTimeTips.completeLabel')}</strong>: {t('clearTimeTips.completeDesc')}
                    </Typography>
                    <Typography variant="body2" paragraph>
                      <strong>{t('clearTimeTips.freeLabel')}</strong>: {t('clearTimeTips.freeDesc')}
                    </Typography>
                  </Box>
                }
              />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2, lineHeight: 1.4 }}>
              {t('clearTimeDesc')}
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>{t('playstyle')}</InputLabel>
              <Select
                value={selectedPlaystyle}
                label={t('playstyle')}
                onChange={(e) => handlePlaystyleChange(e.target.value as number)}
                size="small"
              >
                {Object.keys(PLAYSTYLE_LABELS).map((value) => (
                  <MenuItem key={value} value={parseInt(value)}>
                    {t(`playstyleLabels.${value}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                label={t('hours')}
                type="number"
                value={playtimeHours}
                onChange={(e) => setPlaytimeHours(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                InputProps={{ inputProps: { min: 0 } }}
              />
              <TextField
                label={t('minutes')}
                type="number"
                value={playtimeMinutes}
                onChange={(e) => setPlaytimeMinutes(e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                InputProps={{ inputProps: { min: 0, max: 59 } }}
              />
            </Box>

            <TextField
              label={selectedPlaystyle === 0 ? t('noteRequired') : t('note')}
              value={playtimeNote}
              onChange={(e) => setPlaytimeNote(e.target.value)}
              multiline
              minRows={2}
              maxRows={3}
              fullWidth
              size="small"
              placeholder={selectedPlaystyle === 0 ? t('notePlaceholderFree') : t('notePlaceholder')}
              required={selectedPlaystyle === 0}
              slotProps={{ htmlInput: { maxLength: 500 } }}
              sx={{ mb: 1 }}
            />

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              {playtimeRecords.find(r => r.playstyle === selectedPlaystyle) ? (
                <>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSavePlaytime}
                    fullWidth
                  >
                    {t('editRecord')}
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleDeletePlaytime}
                    color="error"
                    fullWidth
                  >
                    {t('deleteRecord')}
                  </Button>
                </>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleSavePlaytime}
                  fullWidth
                >
                  {t('recordClearTime')}
                </Button>
              )}
            </Box>

            {playtimeRecords.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('registeredRecords')}
                </Typography>
                {playtimeRecords.map((record) => (
                  <Chip
                    key={record.playstyle}
                    label={`${t(`playstyleLabels.${record.playstyle}`)}: ${Math.floor(record.play_time_minutes / 60)}h ${record.play_time_minutes % 60}m`}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))}
              </Box>
            )}
          </Box>
          </>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: fullScreen ? 'calc(8px + env(safe-area-inset-bottom))' : undefined }}>
          <Button onClick={handleSave} color="primary" variant="contained">{t('save')}</Button>
          <Button onClick={requestClose} color="inherit">{t('cancel', { ns: 'common' })}</Button>
        </DialogActions>
      </Dialog>

      <ReviewPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        reviewText={tempData.reviewText || ''}
      />

      {memoEditorOpen && (
        <Suspense fallback={null}>
          <MemoEditorDialog
            open={memoEditorOpen}
            value={memo}
            onSave={async (newValue) => {
              await saveGameMemo(gameId, newValue);
              setMemo(newValue);
              setInitialMemo(newValue);
            }}
            onDelete={async () => {
              await deleteGameMemo(gameId);
              setMemo('');
              setInitialMemo('');
            }}
            onClose={() => setMemoEditorOpen(false)}
            gameId={gameId}
          />
        </Suspense>
      )}
    </>
  );
};

export default GameReviewDialog;